import { useState } from 'react';
import axiosServices from 'utils/axios';

// ==============================|| ADMIN PROFILE UPDATE ||============================== //

export interface UpdateAdminProfileParams {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city_id?: number | null;
  // New API expects `photo` and optional `remove_photo`
  photo?: File;
  remove_photo?: boolean;
}

export interface UpdateAdminProfileResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function useUpdateAdminProfile() {
  const [isLoading, setIsLoading] = useState(false);

  const updateAdminProfile = async (params: UpdateAdminProfileParams): Promise<UpdateAdminProfileResponse> => {
    setIsLoading(true);
    try {
      // If a file is present, send multipart/form-data. Otherwise send JSON body.
      let response;
      if (params.photo) {
        const formData = new FormData();
        if (params.name) formData.append('name', params.name);
        if (params.email) formData.append('email', params.email);
        if (params.phone) formData.append('phone', params.phone);
        if (typeof params.remove_photo === 'boolean') formData.append('remove_photo', String(params.remove_photo));
        formData.append('photo', params.photo);

        response = await axiosServices.patch('/users/me', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const body: any = {};
        if (params.name) body.name = params.name;
        if (params.email) body.email = params.email;
        if (params.phone) body.phone = params.phone;
        if (typeof params.remove_photo === 'boolean') body.remove_photo = params.remove_photo;

        response = await axiosServices.patch('/users/me', body);
      }

      return {
        success: true,
        message: 'Profile updated successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('Error updating admin profile:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to update profile'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateAdminProfile, isLoading };
}

// ==============================|| USER PHOTO ENDPOINTS ||============================== //

export interface UploadUserPhotoResponse {
  success: boolean;
  message: string;
  data?: any;
}

export async function uploadUserPhoto(photo: File): Promise<UploadUserPhotoResponse> {
  try {
    const formData = new FormData();
    formData.append('photo', photo);

    const response = await axiosServices.post('/users/me/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return {
      success: true,
      message: 'Photo uploaded successfully',
      data: response.data
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || 'Failed to upload photo'
    };
  }
}

export interface DeleteUserPhotoResponse {
  success: boolean;
  message: string;
}

export async function deleteUserPhoto(): Promise<DeleteUserPhotoResponse> {
  try {
    await axiosServices.delete('/users/me/photo');
    return { success: true, message: 'Photo deleted successfully' };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || 'Failed to delete photo'
    };
  }
}

// ==============================|| ADMIN CHANGE PASSWORD ||============================== //

export interface ChangeAdminPasswordParams {
  current_password: string;
  password: string;
  password_confirmation: string;
}

export interface ChangeAdminPasswordResponse {
  success: boolean;
  message: string;
}

export function useChangeAdminPassword() {
  const [isLoading, setIsLoading] = useState(false);

  const changeAdminPassword = async (params: ChangeAdminPasswordParams): Promise<ChangeAdminPasswordResponse> => {
    setIsLoading(true);
    try {
      await axiosServices.patch('/account/password', params);

      return { success: true, message: 'Password changed successfully' };
    } catch (error: any) {
      console.error('Error changing admin password:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to change password'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { changeAdminPassword, isLoading };
}

// ==============================|| ADMIN PROFILE INFO ||============================== //

export interface AdminProfileInfo {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  avatar?: string;
  city?: {
    id: number;
    name: string;
  };
}

export interface GetAdminProfileResponse {
  success: boolean;
  message: string;
  data?: AdminProfileInfo;
}

export function useGetAdminProfile() {
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<AdminProfileInfo | null>(null);

  const getAdminProfile = async (): Promise<GetAdminProfileResponse> => {
    setIsLoading(true);
    try {
      const response = await axiosServices.get('/users/me');
      const profileData = response.data?.data || response.data;

      const formattedProfile: AdminProfileInfo = {
        id: profileData.id || profileData.user_id || '',
        name: profileData.name || '',
        email: profileData.email || '',
        phone: profileData.phone || profileData.phone_number || '',
        address: profileData.address || '',
        avatar: profileData.avatar || profileData.profile_photo_url || profileData.profile_photo_path || '',
        city: profileData.city
          ? {
              id: profileData.city.id || profileData.city_id,
              name: profileData.city.name || profileData.city_name || ''
            }
          : profileData.city_id
            ? {
                id: profileData.city_id,
                name: profileData.city_name || ''
              }
            : undefined
      };

      setProfile(formattedProfile);
      return {
        success: true,
        message: 'Profile loaded successfully',
        data: formattedProfile
      };
    } catch (error: any) {
      console.error('Error getting admin profile:', error);
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to load profile'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return { getAdminProfile, profile, isLoading };
}
